/**
 * First-party funnel events for the native inline checkout island.
 *
 * The island renders INSIDE the landing page, so the LP's own tracker
 * (lib/lp-tracking.ts) never sees what happens in it. Without this module the
 * island emits nothing: `checkout_started` and `email_captured` died the day the
 * island replaced the drawer/standalone paths, which also silently switched off
 * abandoned-cart recovery (its session index needs both fields).
 *
 * Same pipe + payload shape as the standalone page's `trackFunnel`
 * (checkout/[slug]/page.tsx), so both surfaces write the SAME
 * lp_visitor_sessions row and the Go handler needs no new event types.
 *
 * Engagement gate: `checkout_started` means checkout ENTRY. On the standalone
 * page arriving IS the intent, so it fires on mount. Here the checkout is a
 * passive section the visitor scrolls past (~19% reach the island root), so
 * mount would only restate scroll depth and would order BEFORE add_to_cart.
 * Entry is therefore the first real interaction, scoped to the island root.
 *
 * The gate deliberately does NOT use `pointerdown` (what the standalone uses):
 * a touch scroll STARTS with pointerdown, so on mobile every visitor who
 * flick-scrolls past the section would count as having entered checkout, which
 * is exactly the scroll-depth restatement this event must not become. `click`
 * is suppressed by the browser once a gesture becomes a scroll, and `focusin`
 * only fires on a real field focus.
 */
import { useCallback, useEffect, useRef } from "react";
import { readLpVid, fireLeadOnce } from "@/lib/meta-funnel";
import { leadValueUsd, LEAD_VALUE_CURRENCY } from "@/lib/lead-value";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type TrackFunnel = (type: string, data?: Record<string, unknown>) => void;

interface LpTrackerGlobals {
  __lpVid?: string;
  /** lib/lp-tracking.ts exposes its batch flush (lp-tracking.ts, `__lpFlush`). */
  __lpFlush?: () => void;
  /**
   * lib/lp-tracking.ts, `__msSetEmail`: publishes a typed address into the LP
   * visitor profile and computes its SHA-256 (em_h) for the capi_fwd backup.
   * Absent off-LP (/checkout standalone), where nothing consumes the profile.
   */
  __msSetEmail?: (email: string) => void;
  /** lib/lp-tracking.ts, `__lpAdIds`: the click-id + cookie snapshot. */
  __lpAdIds?: Record<string, string>;
}

/** Resolved at SEND time, never pinned at mount: the island can mount before the
 *  LP tracker has written the cookie, and a value captured once would then stay
 *  empty for the whole session — silently re-creating the no-events bug. */
function currentVid(): string {
  if (typeof window === "undefined") return "";
  return (window as unknown as LpTrackerGlobals).__lpVid || readLpVid() || "";
}

export function useIslandFunnel({
  apiBase,
  slug,
}: {
  apiBase: string;
  slug: string;
}): { rootRef: React.RefObject<HTMLDivElement | null>; trackFunnel: TrackFunnel } {
  const rootRef = useRef<HTMLDivElement | null>(null);

  // The LP tracker batches on a 5s interval while the island posts immediately.
  // An island event that overtakes the tracker's `page_view` would create the
  // session row WITHOUT utm/referrer/traffic_source (the Go handler reads them
  // only from a page_view in the same batch, and the ON CONFLICT arm backfills
  // just utm_content/utm_term) — a paid visitor would be recorded as `direct`
  // for good. Draining the tracker's queue first keeps attribution intact.
  const flushedRef = useRef(false);
  const flushLpQueueOnce = () => {
    if (flushedRef.current) return;
    flushedRef.current = true;
    try {
      (window as unknown as LpTrackerGlobals).__lpFlush?.();
    } catch {
      /* the LP tracker may not be present (preview/direct mount) */
    }
  };

  // No-ops without an LP visitor id or slug (direct/preview mount) so a missing
  // funnel context can never break the checkout. Best-effort by design.
  const trackFunnel = useCallback<TrackFunnel>(
    (type, data) => {
      const vid = currentVid();
      if (!vid || !slug) return;
      flushLpQueueOnce();
      try {
        fetch(`${apiBase}/api/v1/lp/${encodeURIComponent(slug)}/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            visitor_id: vid,
            events: [{ type, data: data || {}, ts: Date.now() }],
          }),
        }).catch(() => {});
      } catch {
        /* tracking must never block checkout */
      }
    },
    [apiBase, slug],
  );

  // checkout_started — once, on the first real interaction inside the island.
  const startedRef = useRef(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !slug) return;
    const mark = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      trackFunnel("checkout_started", { mode: "island" });
    };
    el.addEventListener("click", mark, { capture: true });
    el.addEventListener("keydown", mark, { capture: true });
    el.addEventListener("focusin", mark, { capture: true });
    return () => {
      el.removeEventListener("click", mark, { capture: true });
      el.removeEventListener("keydown", mark, { capture: true });
      el.removeEventListener("focusin", mark, { capture: true });
    };
  }, [slug, trackFunnel]);

  return { rootRef, trackFunnel };
}

/** email_captured — debounced, once per distinct valid address, so a buyer who
 *  types an email but does not pay becomes abandoned-cart-eligible. Split out so
 *  the caller can pass the live email without re-running the listener effect.
 *
 *  Also publishes the address into the LP visitor profile (`__msSetEmail`), the
 *  island's counterpart to the legacy checkout template's `_captureEmail`. The
 *  Meta funnel events carry the typed email themselves (fwd ctx → _msCapiForward,
 *  F7), so this is what keeps the RELIABLE capi_fwd backup — which may only
 *  carry the hashed em_h — matched as well, and what lets any later LP event
 *  inherit advanced matching. */
export function useEmailCaptured(
  trackFunnel: TrackFunnel,
  email: string,
  lead?: { slug?: string; productId?: string; firstName?: string; lastName?: string },
) {
  const sentRef = useRef("");
  const leadRef = useRef(lead);
  leadRef.current = lead;
  useEffect(() => {
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e) || sentRef.current === e) return;
    const t = setTimeout(() => {
      sentRef.current = e;
      trackFunnel("email_captured", { email: e });
      const g = window as unknown as LpTrackerGlobals;
      try {
        g.__msSetEmail?.(e);
      } catch {
        /* tracking must never break checkout */
      }
      // Meta Lead — the same capture moment, browser pixel + server mirror
      // under one deterministic id. Self-guarded per (visitor, source), so the
      // debounce firing again for a corrected typo does not double-count.
      try {
        fireLeadOnce(
          readLpVid() || g.__lpVid || "",
          "checkout_island",
          {
            adIdentifiers: { ...(g.__lpAdIds || {}) },
            slug: leadRef.current?.slug,
            productId: leadRef.current?.productId,
            email: e,
            firstName: leadRef.current?.firstName?.trim() || undefined,
            lastName: leadRef.current?.lastName?.trim() || undefined,
          },
          // Same value + currency the opt-in Lead carries, so the two Lead
          // sources are consistent and Meta's currency diagnostic stays clear.
          { value: leadValueUsd(), currency: LEAD_VALUE_CURRENCY },
        );
      } catch {
        /* tracking must never break checkout */
      }
    }, 800);
    return () => clearTimeout(t);
  }, [email, trackFunnel]);
}
